import {
  parseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import { PaymentStatus } from '@rematter/frontend-lib-api-client';

import { PaymentRecord } from '../types';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const paymentDetailQueryAtom = createQueryAtomFactory(
  'finance.paymentDetail',
  (params: { id: string }) => ({
    payments: {
      __args: {
        filter: {
          ids: [params.id],
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        transactionDate: true,
        paymentType: true,
        paymentMethod: true,
        checkFacilityId: true,
        checkNumber: true,
        checkMemo: true,
        achNumber: true,
        wireNumber: true,
        creatorId: true,
        creatorPlatform: true,
        financialAccountId: true,
        financialAccountTransactionId: true,
        amount: {
          amount: true,
          currencyCode: true,
        },
        unappliedAmount: {
          amount: true,
          currencyCode: true,
        },
        account: {
          id: true,
          name: true,
          type: true,
        },
        financialAccount: {
          id: true,
          name: true,
          type: true,
        },
        bills: {
          bill: {
            id: true,
            documentNumber: true,
          },
          appliedAmount: {
            amount: true,
            currencyCode: true,
          },
          appliedAt: true,
        },
        invoices: {
          invoice: {
            id: true,
            documentNumber: true,
          },
          appliedAmount: {
            amount: true,
            currencyCode: true,
          },
          appliedAt: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  }),
  {
    select: (data): { payment: PaymentRecord | null } => {
      const entry = data.payments.entries[0];
      if (!entry) {
        return { payment: null };
      }

      return {
        payment: {
          id: entry.id,
          documentNumber: entry.documentNumber,
          transactionDate: parseCalendarDate(entry.transactionDate),
          amount: toMoney(entry.amount),
          unappliedAmount: toMoney(entry.unappliedAmount),
          status: entry.status as PaymentStatus,
          paymentType: entry.paymentType,
          paymentMethod: entry.paymentMethod,
          accountId: entry.account?.id ?? null,
          accountName: entry.account?.name ?? null,
          checkFacilityId: entry.checkFacilityId ?? null,
          checkNumber: entry.checkNumber ?? null,
          checkMemo: entry.checkMemo ?? null,
          achNumber: entry.achNumber ?? null,
          wireNumber: entry.wireNumber ?? null,
          creatorId: entry.creatorId ?? null,
          creatorPlatform: entry.creatorPlatform ?? null,
          financialAccountId: entry.financialAccountId ?? null,
          financialAccountTransactionId:
            entry.financialAccountTransactionId ?? null,
          financialAccount: entry.financialAccount
            ? {
                id: entry.financialAccount.id,
                name: entry.financialAccount.name,
                type: entry.financialAccount.type,
              }
            : null,
          linkedBills: (entry.bills ?? []).map((b) => ({
            id: b.bill.id,
            documentNumber: b.bill.documentNumber,
            appliedAmount: toMoney(b.appliedAmount),
            appliedAt: parseZonedDateTime(b.appliedAt),
          })),
          linkedInvoices: (entry.invoices ?? []).map((inv) => ({
            id: inv.invoice.id,
            documentNumber: inv.invoice.documentNumber,
            appliedAmount: toMoney(inv.appliedAmount),
            appliedAt: parseZonedDateTime(inv.appliedAt),
          })),
          timestamps: {
            createdAt: parseZonedDateTime(entry.timestamps.createdAt),
            updatedAt: parseZonedDateTime(entry.timestamps.updatedAt),
          },
        },
      };
    },
  },
);
